import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL || 'https://ijbc-backend-1089361061119.us-west1.run.app';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
  padding-top: 100px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  color: #333;
  margin-bottom: 30px;
  text-align: center;
`;

const Form = styled.form`
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 40px;
`;

const InputGroup = styled.div`
  margin-bottom: 16px;

  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #444;
  }

  input, textarea {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 15px;
    font-family: 'Noto Sans KR', sans-serif;
    box-sizing: border-box;
  }
`;

const Button = styled.button<{ $variant?: 'danger' | 'secondary' }>`
  background: ${({ $variant }) =>
    $variant === 'danger' ? '#ff4d4f' :
    $variant === 'secondary' ? '#6c757d' : '#2c5aa0'};
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 15px;

  &:hover { opacity: 0.88; }
  &:disabled { background: #ccc; cursor: default; }
`;

const DropZone = styled.label<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 2px dashed ${({ $active }) => ($active ? '#2c5aa0' : '#ccc')};
  border-radius: 8px;
  padding: 32px 20px;
  background: ${({ $active }) => ($active ? '#f0f4ff' : '#fafafa')};
  cursor: pointer;
  transition: all 0.2s;
  color: #888;
  font-size: 15px;

  &:hover {
    border-color: #2c5aa0;
    background: #f0f4ff;
    color: #2c5aa0;
  }
`;

const PreviewGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const PreviewThumb = styled.div`
  position: relative;
  width: 90px;
  height: 90px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #ddd;
  }

  button {
    position: absolute;
    top: -6px;
    right: -6px;
    width: 20px;
    height: 20px;
    background: #ff4d4f;
    color: white;
    border: none;
    border-radius: 50%;
    font-size: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    line-height: 1;
  }
`;

const StatusMsg = styled.p<{ $error?: boolean }>`
  font-size: 14px;
  color: ${({ $error }) => ($error ? '#e53935' : '#2e7d32')};
  margin-top: 10px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Item = styled.div`
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.07);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const ItemInfo = styled.div`
  flex: 1;

  h3 {
    margin: 0 0 4px 0;
    font-size: 16px;
    color: #333;
  }

  p {
    margin: 0;
    color: #888;
    font-size: 13px;
  }
`;

const Thumbs = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 8px;
  flex-wrap: wrap;

  img {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #eee;
  }
`;

interface MediaItem {
  id: string;
  title: string;
  date: string;
  images: { fileName: string; fileUrl: string }[];
  youtubeId?: string;
  uploadDate: string;
}

const defaultForm = () => ({
  title: '',
  date: new Date().toISOString().slice(0, 10),
  youtubeId: '',
});

const AdminMedia: React.FC = () => {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [form, setForm] = useState(defaultForm());
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [statusError, setStatusError] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getApiKey = () =>
    localStorage.getItem('adminToken') ||
    process.env.REACT_APP_ADMIN_API_KEY ||
    'ijbc-admin-secret-key-2025';

  const fetchItems = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/media`);
      const data = await res.json();
      if (data.success) setItems(data.media || []);
    } catch (err) {
      console.error('Failed to fetch media:', err);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    setSelectedFiles(prev => {
      const next = [...prev, ...imageFiles];
      // Build previews
      imageFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviews(p => [...p, e.target?.result as string]);
        };
        reader.readAsDataURL(file);
      });
      return next;
    });
  };

  const removeFile = (idx: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title) { setStatusError(true); setStatusMsg('제목을 입력해 주세요.'); return; }
    if (selectedFiles.length === 0 && !form.youtubeId) {
      setStatusError(true); setStatusMsg('사진 또는 YouTube ID를 입력해 주세요.'); return;
    }

    setLoading(true);
    setStatusMsg('');
    setStatusError(false);

    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('date', form.date);
      if (form.youtubeId.trim()) fd.append('youtubeId', form.youtubeId.trim());
      selectedFiles.forEach(f => fd.append('files', f));

      const res = await fetch(`${API_URL}/api/upload-media`, {
        method: 'POST',
        headers: { 'x-api-key': getApiKey() },
        body: fd,
      });
      const data = await res.json();
      if (data.success) {
        setForm(defaultForm());
        setSelectedFiles([]);
        setPreviews([]);
        setStatusMsg('업로드 완료!');
        fetchItems();
      } else {
        setStatusError(true);
        setStatusMsg(data.error || '업로드 실패');
      }
    } catch (err: any) {
      setStatusError(true);
      setStatusMsg(err.message || '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('이 항목을 삭제하시겠습니까?')) return;
    try {
      const res = await fetch(`${API_URL}/api/media/${id}`, {
        method: 'DELETE',
        headers: { 'x-api-key': getApiKey() },
      });
      const data = await res.json();
      if (data.success) fetchItems();
      else alert(data.error || '삭제 실패');
    } catch (err) {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  return (
    <Container>
      <PageTitle>사진/영상 관리</PageTitle>

      <Form onSubmit={handleSubmit}>
        <h3 style={{ marginBottom: '20px', color: '#2c5aa0' }}>새 앨범 등록</h3>

        <InputGroup>
          <label>제목</label>
          <input
            type="text"
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder="예: 2025년 부활절 예배"
            required
          />
        </InputGroup>

        <InputGroup>
          <label>날짜</label>
          <input
            type="date"
            value={form.date}
            onChange={e => setForm({ ...form, date: e.target.value })}
            required
          />
        </InputGroup>

        <InputGroup>
          <label>YouTube ID (선택)</label>
          <input
            type="text"
            value={form.youtubeId}
            onChange={e => setForm({ ...form, youtubeId: e.target.value })}
            placeholder="예: dQw4w9WgXcQ (YouTube URL의 마지막 부분)"
          />
        </InputGroup>

        <InputGroup>
          <label>사진 (최대 20장, 각 10MB)</label>
          <DropZone
            $active={dragOver}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={e => addFiles(e.target.files)}
            />
            <span style={{ fontSize: 32 }}>📷</span>
            <span>클릭하거나 사진을 여기에 드래그하세요</span>
            <span style={{ fontSize: 13 }}>JPG, PNG, GIF, WEBP 지원</span>
          </DropZone>

          {previews.length > 0 && (
            <PreviewGrid>
              {previews.map((src, i) => (
                <PreviewThumb key={i}>
                  <img src={src} alt="" />
                  <button type="button" onClick={() => removeFile(i)}>✕</button>
                </PreviewThumb>
              ))}
            </PreviewGrid>
          )}
        </InputGroup>

        <Button type="submit" disabled={loading}>
          {loading ? '업로드 중...' : '앨범 등록'}
        </Button>
        {statusMsg && <StatusMsg $error={statusError}>{statusMsg}</StatusMsg>}
      </Form>

      <h2 style={{ marginBottom: '16px', color: '#333' }}>등록된 앨범 ({items.length}건)</h2>
      <List>
        {items.map(item => (
          <Item key={item.id}>
            <ItemInfo>
              <h3>{item.title}</h3>
              <p>
                {item.date}
                {item.youtubeId && ' · YouTube'}
                {item.images.length > 0 && ` · 사진 ${item.images.length}장`}
              </p>
              {item.images.length > 0 && (
                <Thumbs>
                  {item.images.slice(0, 5).map((img, i) => (
                    <img key={i} src={img.fileUrl} alt="" />
                  ))}
                  {item.images.length > 5 && (
                    <span style={{ fontSize: 12, color: '#888', alignSelf: 'center' }}>
                      +{item.images.length - 5}
                    </span>
                  )}
                </Thumbs>
              )}
            </ItemInfo>
            <Button $variant="danger" onClick={() => handleDelete(item.id)}>삭제</Button>
          </Item>
        ))}
      </List>
    </Container>
  );
};

export default AdminMedia;
